import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, CheckCircle2, Users, XCircle, AlertTriangle, X } from "lucide-react";

export default function TeacherCoursesPanel() {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    subject: "",
    num_sessions: 5,
    fees: 999,
    duration_per_session_min: 60,
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/teacher/courses"), api.get("/teacher/enrollments")])
      .then(([c, e]) => {
        if (cancelled) return;
        setCourses(c.data || []);
        setEnrollments(e.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    try {
      const [c, e] = await Promise.all([api.get("/teacher/courses"), api.get("/teacher/enrollments")]);
      setCourses(c.data || []);
      setEnrollments(e.data || []);
    } catch { /* silent */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.num_sessions < 1) return toast.error("Number of sessions must be at least 1");
    setSaving(true);
    try {
      await api.post("/teacher/courses", {
        ...form,
        num_sessions: parseInt(form.num_sessions, 10),
        fees: parseInt(form.fees, 10),
        duration_per_session_min: parseInt(form.duration_per_session_min, 10),
      });
      toast.success("Course created");
      setShowForm(false);
      setForm({ title: "", description: "", subject: "", num_sessions: 5, fees: 999, duration_per_session_min: 60 });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create course");
    } finally {
      setSaving(false);
    }
  };

  const removeCourse = async (id) => {
    if (!confirm("Delete this course? (Will be soft-deleted if any enrollment exists)")) return;
    try {
      await api.delete(`/teacher/courses/${id}`);
      toast.success("Course removed");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const [punchInTarget, setPunchInTarget] = useState(null);
  const [punchForm, setPunchForm] = useState({ writeup: "", skill_tags: "" });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const openCancel = (enrollment) => {
    setCancelTarget(enrollment);
    setCancelReason("");
  };
  const closeCancel = () => {
    setCancelTarget(null);
    setCancelReason("");
  };
  const submitCancelCourse = async (ev) => {
    ev?.preventDefault?.();
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api.post(`/teacher/enrollments/${cancelTarget.id}/cancel-course`, {
        reason: cancelReason,
      });
      toast.success("Course cancelled. Parent will be refunded; platform charge recorded.");
      closeCancel();
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel course");
    } finally {
      setCancelling(false);
    }
  };

  const openPunchIn = (enrollment) => {
    setPunchInTarget(enrollment);
    setPunchForm({ writeup: "", skill_tags: "" });
  };
  const closePunchIn = () => setPunchInTarget(null);

  const submitPunchIn = async (e) => {
    e.preventDefault();
    if (!punchForm.writeup.trim()) return toast.error("Write-up is required");
    try {
      await api.post(`/teacher/enrollments/${punchInTarget.id}/punch-in`, {
        writeup: punchForm.writeup.trim(),
        skill_tags: punchForm.skill_tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Punched in — explorer has been notified to confirm & rate");
      closePunchIn();
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const markComplete = openPunchIn; // alias to keep prop name

  return (
    <div className="mt-8 bg-white rounded-3xl p-6 border border-black/5" data-testid="courses-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-xl font-bold uppercase inline-flex items-center gap-2" style={{ color: "#13A699" }}>
          <BookOpen className="h-5 w-5" /> My Courses
        </h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="btn-yellow text-xs px-3 py-2 inline-flex items-center gap-1"
          data-testid="new-course-btn"
        >
          <Plus className="h-3.5 w-3.5" /> {showForm ? "Cancel" : "New Course"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="new-course-form">
          <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="course-title" />
          <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="course-subject" />
          <Field label="Number of sessions" type="number" min="1" value={form.num_sessions} onChange={(v) => setForm({ ...form, num_sessions: v })} testId="course-num" />
          <Field label="Total fees (₹)" type="number" min="0" value={form.fees} onChange={(v) => setForm({ ...form, fees: v })} testId="course-fees" />
          <Field label="Minutes per session" type="number" min="15" value={form.duration_per_session_min} onChange={(v) => setForm({ ...form, duration_per_session_min: v })} testId="course-dur" />
          <label className="sm:col-span-2 block text-sm font-semibold" style={{ color: "#13A699" }}>
            Description
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-[#13A699] focus:ring-2 focus:ring-[#13A699] outline-none"
              data-testid="course-desc"
            />
          </label>
          <div className="sm:col-span-2 flex justify-end">
            <button type="submit" disabled={saving} className="btn-teal text-sm px-4 py-2" data-testid="save-course-btn">
              {saving ? "Saving…" : "Save Course"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="mt-4 text-sm" style={{ color: "#13A69988" }}>Loading…</p>
      ) : courses.length === 0 ? (
        <p className="mt-4 text-sm" style={{ color: "#13A69988" }}>No courses yet. Create your first multi-session package.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {courses.map((c) => (
            <li
              key={c.id}
              className="rounded-2xl border border-black/5 p-3 flex flex-wrap items-center justify-between gap-2 bg-[#FFF7ED]"
              data-testid={`course-row-${c.id}`}
            >
              <div className="min-w-0">
                <div className="font-display font-bold" style={{ color: "#13A699" }}>{c.title}</div>
                <div className="text-xs" style={{ color: "#13A69988" }}>
                  {c.subject || "—"} • {c.num_sessions} sessions • ₹{c.fees} {c.active ? "" : "• inactive"}
                </div>
              </div>
              <button
                onClick={() => removeCourse(c.id)}
                className="text-xs px-2 py-1 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                data-testid={`delete-course-${c.id}`}
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Enrollments */}
      <h3 className="font-display font-bold mt-6 uppercase tracking-wider inline-flex items-center gap-2" style={{ color: "#13A699" }}>
        <Users className="h-4 w-4" /> Enrollments
      </h3>
      {enrollments.length === 0 ? (
        <p className="mt-2 text-sm" style={{ color: "#13A69988" }}>No enrollments yet.</p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="enrollments-list">
          {enrollments.map((e) => (
            <li
              key={e.id}
              className="rounded-2xl border border-black/5 p-3 flex flex-wrap items-center justify-between gap-2"
              data-testid={`enrollment-${e.id}`}
            >
              <div className="min-w-0">
                <div className="font-display font-bold" style={{ color: "#13A699" }}>{e.course_title}</div>
                <div className="text-xs" style={{ color: "#13A69988" }}>
                  Explorer: {e.parent_name} {e.child_snapshot?.name ? `• Child: ${e.child_snapshot.name}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={e.status} />
                {e.status === "active" && (
                  <>
                    <button
                      onClick={() => markComplete(e)}
                      className="text-xs px-3 py-1.5 rounded-xl border border-[#13A699] text-[#13A699] hover:bg-[#13A699] hover:text-white inline-flex items-center gap-1"
                      data-testid={`punch-in-${e.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Punch In
                    </button>
                    <button
                      onClick={() => openCancel(e)}
                      className="text-xs px-3 py-1.5 rounded-xl border border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                      data-testid={`cancel-course-${e.id}`}
                    >
                      <XCircle className="h-3.5 w-3.5" /> Cancel Course
                    </button>
                  </>
                )}
                {e.rated && <span className="text-xs font-bold" style={{ color: "#13A699" }}>★ {e.rating}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Punch-in modal */}
      {punchInTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="punch-in-modal">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
            <h2 className="font-display text-xl font-bold" style={{ color: "#13A699" }}>
              Punch In: {punchInTarget.course_title}
            </h2>
            <p className="text-sm mt-1" style={{ color: "#13A699AA" }}>
              Write a short note about the child's progress + add 2–5 skill tags they built. This is shown on their Performance chart.
            </p>
            <form onSubmit={submitPunchIn} className="mt-4 space-y-3">
              <label className="block text-sm font-semibold" style={{ color: "#13A699" }}>
                Write-up (visible to explorer)
                <textarea
                  rows={4}
                  value={punchForm.writeup}
                  onChange={(ev) => setPunchForm({ ...punchForm, writeup: ev.target.value })}
                  placeholder="e.g. Aarav showed strong improvement in algebra fundamentals…"
                  className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-[#13A699] focus:ring-2 focus:ring-[#13A699] outline-none"
                  data-testid="punch-writeup"
                  required
                />
              </label>
              <label className="block text-sm font-semibold" style={{ color: "#13A699" }}>
                Skills developed (comma-separated)
                <input
                  type="text"
                  value={punchForm.skill_tags}
                  onChange={(ev) => setPunchForm({ ...punchForm, skill_tags: ev.target.value })}
                  placeholder="Algebra, Critical thinking, Problem solving"
                  className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-[#13A699] focus:ring-2 focus:ring-[#13A699] outline-none"
                  data-testid="punch-skills"
                />
              </label>
              <div className="flex justify-end gap-2 mt-3">
                <button type="button" onClick={closePunchIn} className="text-sm px-3 py-2 rounded-xl border border-black/10" data-testid="punch-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-teal text-sm px-4 py-2" data-testid="punch-submit">
                  Punch In
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Cancel-course modal */}
      {cancelTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="cancel-course-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeCancel(); }}
        >
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-display text-xl font-bold inline-flex items-center gap-2" style={{ color: "#DC2626" }}>
                <AlertTriangle className="h-5 w-5" /> Cancel Entire Course?
              </h2>
              <button onClick={closeCancel} className="text-[#13A699]" data-testid="cancel-course-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm mt-2" style={{ color: "#13A699AA" }}>
              <b>{cancelTarget.course_title}</b> · Parent: {cancelTarget.parent_name}
              {cancelTarget.child_snapshot?.name ? ` · Child: ${cancelTarget.child_snapshot.name}` : ""}
            </p>
            <div className="mt-4 rounded-2xl p-3 text-xs" style={{ background: "#FFF7ED" }}>
              <div className="font-bold uppercase" style={{ color: "#DC2626" }}>
                Cancellation impact
              </div>
              <ul className="mt-2 space-y-1" style={{ color: "#13A699" }}>
                <li>• Parent gets a <b>full refund of ₹{cancelTarget.fees}</b>.</li>
                <li>• You will be charged <b>20% (₹{Math.round((cancelTarget.fees || 0) * 0.20)})</b> of the course fee by the platform.</li>
                <li>• Any unpunched sessions in this enrollment are voided.</li>
              </ul>
            </div>

            <label className="block mt-4 text-xs font-bold uppercase" style={{ color: "#13A699" }}>
              Reason (optional)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
              placeholder="e.g. relocating, unable to continue"
              className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm bg-white text-[#13A699] focus:ring-2 focus:ring-[#13A699] outline-none"
              data-testid="cancel-course-reason"
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeCancel}
                className="text-sm font-bold px-4 py-2 rounded-full border border-[#13A699]"
                style={{ color: "#13A699" }}
                data-testid="cancel-course-keep"
              >
                Keep Course
              </button>
              <button
                type="button"
                onClick={submitCancelCourse}
                disabled={cancelling}
                className="text-sm font-bold px-4 py-2 rounded-full bg-red-600 text-white disabled:opacity-60"
                data-testid="cancel-course-confirm"
              >
                {cancelling ? "Cancelling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Active", bg: "#FFD708", color: "#13A699" },
    teacher_completed: { label: "Awaiting explorer", bg: "#FBA31222", color: "#FBA312" },
    completed: { label: "Completed", bg: "#13A69922", color: "#13A699" },
    cancelled: { label: "Cancelled", bg: "#DC262622", color: "#DC2626" },
    cancelled_by_teacher: { label: "Cancelled by you", bg: "#DC262622", color: "#DC2626" },
  };
  const s = map[status] || map.active;
  return (
    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function Field({ label, value, onChange, type = "text", testId, ...rest }) {
  return (
    <label className="block text-sm font-semibold" style={{ color: "#13A699" }}>
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-[#13A699] focus:ring-2 focus:ring-[#13A699] outline-none"
        data-testid={testId}
        {...rest}
      />
    </label>
  );
}
